import { permissionCategories, type PermissionCategory } from '../permissionCategories';
import type { PermissionState } from './permissionState';

export interface CategorySummaryCount {
  enabled: number;
  total: number;
}

const codePermissionCount = (categoryId: string, enabledPermissions: Set<string>): CategorySummaryCount => {
  const category = permissionCategories.find(c => c.id === categoryId);
  if (!category) return { enabled: 0, total: 0 };
  const allPerms = category.permissions;
  return { enabled: allPerms.filter(p => enabledPermissions.has(p.id)).length, total: allPerms.length };
};

const allCategoryPermissions = (category: PermissionCategory) =>
  category.groups ? category.groups.flatMap(g => g.permissions) : category.permissions;

export const isCategoryFullyEnabled = (category: PermissionCategory, enabledPermissions: Set<string>): boolean => {
  const allPerms = allCategoryPermissions(category);
  return allPerms.length > 0 && allPerms.every(p => enabledPermissions.has(p.id));
};

export const getCategoryEnabledCount = (category: PermissionCategory, enabledPermissions: Set<string>): number =>
  allCategoryPermissions(category).filter(p => enabledPermissions.has(p.id)).length;

export const getCategorySummaryCount = (categoryId: string, permissions: PermissionState): CategorySummaryCount => {
  const { enabledPermissions } = permissions;
  switch (categoryId) {
    case 'dashboard':
      return codePermissionCount('dashboard', enabledPermissions);
    case 'timesheet': {
      let enabled = 0;
      if (permissions.viewTimesheetsAccess !== 'none') enabled++;
      if (permissions.editTimesheetsAccess !== 'none') enabled++;
      if (permissions.allowDeleteTimesheets) enabled++;
      if (permissions.allowDuplicateTimesheets) enabled++;
      if (permissions.allowRecallTimesheet) enabled++;
      if (permissions.enableCopyFromLastWeek) enabled++;
      if (permissions.enableCopyDescription) enabled++;
      if (permissions.enableCopyOnlyBillable) enabled++;
      if (permissions.enableAutoSmartFill) enabled++;
      if (permissions.enableAiInsights) enabled++;
      return { enabled, total: 10 };
    }
    case 'approvals': {
      let enabled = 0;
      if (permissions.viewApprovalDashboard) enabled++;
      if (permissions.viewApprovals) enabled++;
      if (permissions.editTimesheetsApproval) enabled++;
      if (permissions.rejectTimesheets) enabled++;
      if (permissions.compulsoryRejectionReason) enabled++;
      if (permissions.bulkApprove) enabled++;
      if (permissions.delegateApproval) enabled++;
      if (permissions.viewPolicyViolations) enabled++;
      if (permissions.viewDetectedIssues) enabled++;
      return { enabled, total: 9 };
    }
    case 'reports': {
      let enabled = 0;
      if (permissions.enableTimesheetReport) enabled++;
      if (permissions.allowTimesheetReportExport) enabled++;
      if (permissions.enableMissingTimesheetReport) enabled++;
      if (permissions.allowMissingTimesheetReportExport) enabled++;
      if (permissions.enableUtilisationReport) enabled++;
      if (permissions.allowUtilisationReportExport) enabled++;
      return { enabled, total: 6 };
    }
    case 'team': {
      let enabled = 0;
      if (permissions.viewEmployeeDirectory) enabled++;
      if (permissions.viewEmployeeDetails) enabled++;
      return { enabled, total: 2 };
    }
    case 'projects':
      return codePermissionCount('projects', enabledPermissions);
    case 'integrations': {
      let enabled = 0;
      if (permissions.viewIntegrations) enabled++;
      if (permissions.manageIntegrations) enabled++;
      if (permissions.viewCompanies) enabled++;
      if (permissions.manageCompanies) enabled++;
      return { enabled, total: 4 };
    }
    case 'settings': {
      let enabled = 0;
      if (permissions.manageGeneralSettings === 'configure') enabled++;
      if (permissions.manageDeadlines) enabled++;
      if (permissions.managePolicySettings) enabled++;
      if (permissions.manageUtilisationSettings) enabled++;
      if (permissions.manageTimeEntrySettings) enabled++;
      if (permissions.manageWorkTypeSettings) enabled++;
      if (permissions.manageApprovalsSettings) enabled++;
      if (permissions.manageNotifications) enabled++;
      if (permissions.manageRolesPermissions) enabled++;
      if (permissions.manageRates) enabled++;
      return { enabled, total: 10 };
    }
    case 'data':
      return codePermissionCount('data', enabledPermissions);
    default:
      return { enabled: 0, total: 0 };
  }
};

export const getTotalPermissionsCount = (permissions: PermissionState): number => {
  let count = permissions.enabledPermissions.size;
  if (permissions.viewTimesheetsAccess !== 'none') count++;
  if (permissions.editTimesheetsAccess !== 'none') count++;
  if (permissions.allowDeleteTimesheets) count++;
  if (permissions.allowDuplicateTimesheets) count++;
  if (permissions.allowRecallTimesheet) count++;
  if (permissions.enableCopyFromLastWeek) count++;
  if (permissions.enableCopyDescription) count++;
  if (permissions.enableCopyOnlyBillable) count++;
  if (permissions.enableAutoSmartFill) count++;
  if (permissions.enableAiInsights) count++;
  if (permissions.viewApprovalDashboard) count++;
  if (permissions.viewApprovals) count++;
  if (permissions.editTimesheetsApproval) count++;
  if (permissions.rejectTimesheets) count++;
  if (permissions.compulsoryRejectionReason) count++;
  if (permissions.bulkApprove) count++;
  if (permissions.delegateApproval) count++;
  if (permissions.viewPolicyViolations) count++;
  if (permissions.viewDetectedIssues) count++;
  if (permissions.enableTimesheetReport) count++;
  if (permissions.allowTimesheetReportExport) count++;
  if (permissions.enableMissingTimesheetReport) count++;
  if (permissions.allowMissingTimesheetReportExport) count++;
  if (permissions.enableUtilisationReport) count++;
  if (permissions.allowUtilisationReportExport) count++;
  if (permissions.viewEmployeeDirectory) count++;
  if (permissions.viewEmployeeDetails) count++;
  if (permissions.viewIntegrations) count++;
  if (permissions.manageIntegrations) count++;
  if (permissions.viewCompanies) count++;
  if (permissions.manageCompanies) count++;
  if (permissions.manageGeneralSettings === 'configure') count++;
  if (permissions.manageDeadlines) count++;
  if (permissions.managePolicySettings) count++;
  if (permissions.manageUtilisationSettings) count++;
  if (permissions.manageTimeEntrySettings) count++;
  if (permissions.manageWorkTypeSettings) count++;
  if (permissions.manageApprovalsSettings) count++;
  if (permissions.manageNotifications) count++;
  if (permissions.manageRolesPermissions) count++;
  if (permissions.manageRates) count++;
  // manageScreenDataModes excluded from count — hidden from UI
  return count;
};

export const getTabPermissionCount = (tab: string, permissions: PermissionState): number => {
  switch (tab) {
    case 'time-tracking': {
      let c = 0;
      if (permissions.enabledPermissions.has('dashboard.view_own')) c++;
      if (permissions.enabledPermissions.has('dashboard.view_team')) c++;
      if (permissions.enabledPermissions.has('dashboard.view_department')) c++;
      if (permissions.viewTimesheetsAccess !== 'none') c++;
      if (permissions.editTimesheetsAccess !== 'none') c++;
      if (permissions.allowDeleteTimesheets) c++;
      if (permissions.allowDuplicateTimesheets) c++;
      if (permissions.allowRecallTimesheet) c++;
      if (permissions.enableCopyFromLastWeek) c++;
      if (permissions.enableCopyDescription) c++;
      if (permissions.enableCopyOnlyBillable) c++;
      if (permissions.enableAutoSmartFill) c++;
      if (permissions.enableAiInsights) c++;
      return c;
    }
    case 'approval': {
      let c = 0;
      if (permissions.viewApprovalDashboard) c++;
      if (permissions.viewApprovals) c++;
      if (permissions.editTimesheetsApproval) c++;
      if (permissions.rejectTimesheets) c++;
      if (permissions.compulsoryRejectionReason) c++;
      if (permissions.bulkApprove) c++;
      if (permissions.delegateApproval) c++;
      if (permissions.viewPolicyViolations) c++;
      if (permissions.viewDetectedIssues) c++;
      return c;
    }
    case 'reports': {
      let c = 0;
      if (permissions.enableTimesheetReport) c++;
      if (permissions.allowTimesheetReportExport) c++;
      if (permissions.enableMissingTimesheetReport) c++;
      if (permissions.allowMissingTimesheetReportExport) c++;
      if (permissions.enableUtilisationReport) c++;
      if (permissions.allowUtilisationReportExport) c++;
      return c;
    }
    case 'management': {
      let c = 0;
      if (permissions.viewEmployeeDirectory) c++;
      if (permissions.viewEmployeeDetails) c++;
      return c;
    }
    case 'data': {
      let c = 0;
      if (permissions.viewIntegrations) c++;
      if (permissions.manageIntegrations) c++;
      if (permissions.viewCompanies) c++;
      if (permissions.manageCompanies) c++;
      return c;
    }
    case 'administration': {
      let c = 0;
      if (permissions.manageGeneralSettings === 'configure') c++;
      if (permissions.manageDeadlines) c++;
      if (permissions.managePolicySettings) c++;
      if (permissions.manageUtilisationSettings) c++;
      if (permissions.manageTimeEntrySettings) c++;
      if (permissions.manageWorkTypeSettings) c++;
      if (permissions.manageApprovalsSettings) c++;
      if (permissions.manageNotifications) c++;
      if (permissions.manageRolesPermissions) c++;
      if (permissions.manageRates) c++;
      // manageScreenDataModes excluded from count — hidden from UI
      return c;
    }
    default:
      return 0;
  }
};
